import {IBenchmarkReturn, IPlanTreeNode} from "../../model/Portfolio.model";
import * as React from "react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    TooltipContentProps,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer, LabelList, ReferenceLine,
} from "recharts";
import {ISelectValue} from "../common/Select.component";
import {CustomChartTooltip, ICustomTooltipProps} from "../common/HorizontalBarChart.component";
import {AonColors} from "../../../../css/Colors";
import {useEffect} from "react";
import {getDatedBenchmarkReturn, getTotalNode, IBarChartData, mapPerformanceChartData} from "./portfolioUtil";

export interface IPerformanceSummaryChartProps {
    selectedDate: ISelectValue;
    planTreeNodes: IPlanTreeNode[];
    benchmarkReturns: IBenchmarkReturn[];
}

export const PerformanceSummaryChart: React.FunctionComponent<IPerformanceSummaryChartProps> = (props) => {
    const [mapData, setMapData] = React.useState<IBarChartData[]>([]);
    const [totalPlanName, setTotalPlanName] = React.useState<string>("");
    const [benchmarkName, setBenchmarkName] = React.useState<string | undefined>("");

    useEffect(() => {
        const datedTotalPlanNode
            = getTotalNode(props.planTreeNodes, props.selectedDate.id!.toString()) as IPlanTreeNode | undefined;
        if (datedTotalPlanNode?.benchmarkId) {
            const datedBenchmark = getDatedBenchmarkReturn(
                props.selectedDate.id!.toString(), datedTotalPlanNode.benchmarkId, props.benchmarkReturns);
            setMapData(datedBenchmark
                ? mapPerformanceChartData(datedTotalPlanNode.absolutePerformance, datedBenchmark.performance)
                : []);
            setTotalPlanName(datedTotalPlanNode.name);
            setBenchmarkName(datedBenchmark?.name);
        } else {
            setMapData([]);
        }
    }, [props.selectedDate, props.planTreeNodes, props.benchmarkReturns]);

    const getCustomTooltip = (tooltipProps: TooltipContentProps): React.ReactNode => {
        const {active, payload} = tooltipProps;
        if (active && payload && payload.length) {
            const customTooltipProps: ICustomTooltipProps = {
                title: payload[0].payload.name,
                label: `${totalPlanName}: ${typeof payload[0].value === "number" && !isNaN(payload[0].value) ? `${payload[0].value.toFixed(2)}%` : payload[0].value}`,
                value: `${benchmarkName}: ${typeof payload[1].value === "number" && !isNaN(payload[1].value) ? `${payload[1].value.toFixed(2)}%` : payload[1].value}`
            };
            return <CustomChartTooltip {...customTooltipProps} />;
        }
        return null;
    };

    const renderChart = () => {
        return <div className="performance-summary-chart__chart-container" data-testid="performance-summary-chart__chart-container">
            <ResponsiveContainer width="100%" height={400}>
                <BarChart
                    data={mapData}
                    margin={{
                        top: 20,
                        right: 70,
                        left: 20,
                        bottom: 5,
                    }}
                    barGap={18}
                >
                    <CartesianGrid stroke="#ccc" vertical={false}/>
                    <XAxis
                        dataKey="name"
                        className={"performance-summary-chart__axis-label"}
                        tickLine={false}
                        axisLine={false}
                        tick={{ dy: 5 }}
                    />
                    <YAxis
                        className={"performance-summary-chart__axis-label"}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => typeof value === "number" && !isNaN(value) ? `${value.toFixed(2)}%` : value}
                    />
                    <RechartsTooltip content={getCustomTooltip}/>
                    <ReferenceLine y={0} stroke={AonColors.AonBlack}/>
                    <Legend iconType="circle"
                        wrapperStyle={{bottom: -14}}
                        formatter={(value) => {
                            return (
                                <span className="performance-summary-chart__legend-text">
                                {value === "absolutePerformanceValue" ? `${totalPlanName}` : `${benchmarkName}`}
                            </span>
                            );
                        }}
                    />
                    <Bar dataKey="absolutePerformanceValue" fill={AonColors.AonDataBlue} barSize={30}>
                        <LabelList
                            dataKey="absolutePerformanceValue"
                            position="top"
                            className={"performance-summary-chart__bar-label"}
                            formatter={(value) => typeof value === "number" && !isNaN(value) ? `${value.toFixed(2)}%` : String(value ?? "")}
                        />
                    </Bar>
                    <Bar dataKey="benchmarkPerformanceValue" fill={AonColors.AonTealLight} barSize={30}>
                        <LabelList
                            dataKey="benchmarkPerformanceValue"
                            position="top"
                            className={"performance-summary-chart__bar-label"}
                            formatter={(value) => typeof value === "number" && !isNaN(value) ? `${value.toFixed(2)}%` : String(value ?? "")}
                        />
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>;
    };

    const renderComponent = () => {
        return mapData.length > 0
            ? <div className="performance-summary-chart__container" data-testid="performance-summary-chart__container">
                <h2>Total Plan Performance Summary</h2>
                {mapData.length > 0 && renderChart()}
              </div>
            : null;
    };

    return renderComponent();
};